import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IInboxJob } from '../interfaces/inbox-job';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { ITaskInfo, IVehicleAddress } from '../interfaces/task-info';
import { RouteDirectionRequest } from '../interfaces/route-direction-request';
import { GeocoderResponse } from '../interfaces/geocoder-response.model';
import { Observable } from 'rxjs';
import { BaseDataService } from './base.data.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { AddressDTO } from '../interfaces/address';
import { InteractiveMapData } from '../interfaces/interactive-map';
import { environment } from '../../environments/environment';

export interface IStaticMapTask {
  polyline?: string;
  id: number;
  pickupLocation: { latitude: number; longitude: number; postcode?: string };
  dropoffLocation: { latitude: number; longitude: number; postcode?: string };
}

export interface IStaticMapFromTasksRequest {
  jobId: number;
  taskIds: number[];
  includePauses: boolean;
  includeTracking: boolean;
  options: {
    mapSize: string;
    scale: number;
  };
}

@Injectable({ providedIn: 'root' })
export class MapService extends BaseDataService {
  private logger: TaggedLogger;

  host =
    location.hostname.indexOf('localhost') > -1
      ? `${location.protocol}//${location.hostname}:${location.port}`
      : `${location.protocol}//${location.hostname}`;
  baseUrl = 'api/map';

  constructor(
    public http: HttpClient,
    loggerService: LoggerService,
    handlersFactory: HttpResponsesHandlersFactory,
  ) {
    super(http, handlersFactory);
    this.logger = loggerService.taggedLogger('MapService');
  }

  public DirectionColours = [
    '0x0082CBB2',
    '0xF97316B2',
    '0x634ECAB2',
    '0xDC3535B2',
    '0x111111B2',
    '0x8D544EB2',
  ];

  public GetRouteDTO(
    pickup: IVehicleAddress,
    dropoff: IVehicleAddress,
    departure: Date,
  ): RouteDirectionRequest {
    return {
      origin: { lat: pickup.latitude, lng: pickup.longitude },
      destination: { lat: dropoff.latitude, lng: dropoff.longitude },
      earliestDeparture: departure,
    };
  }

  public GetRoute(request: RouteDirectionRequest) {
    const queryString = this.toQueryString(request);
    let url = `api/map/route?routeRequest=${queryString}`;

    return this.http.get<any>(url).toPromise();
  }

  public getStaticMapUrlFromTasks(request: IStaticMapFromTasksRequest): Promise<string> {
    const params = this.constructHttpParams(request);
    return this.http
      .get('/api/map/static-map-for-tasks', { params, responseType: 'text', observe: 'body' })
      .toPromise();
  }

  public getStaticMapWithMarkers(
    tasks: IStaticMapTask[],
    options: { mapSize: string; selectedTaskId: any; scale?: 1 | 2 },
  ) {
    const lineWeight = options['lineWeight'] || 3;

    let path: { [key: number]: string } = {};
    let cnt = 0;
    let markers = this.buildAlphaMarkers(tasks);
    let plotPaths: string[] = [];
    let plotMarkers: string[] = [];

    // This builds alpha paths, we could put it in a function
    tasks.forEach((task: IStaticMapTask) => {
      path[task.id] =
        'color:' +
        this.DirectionColours[cnt] +
        '|' +
        `weight:${lineWeight} + ` +
        '|' +
        'enc:' +
        task.polyline;
      cnt++;
    });

    // Once we have assembled the paths, we can then filter out the ones we want to plot
    tasks.forEach((task) => {
      if (!options['selectedTaskId'] || task.id == options['selectedTaskId']) {
        plotPaths.push(path[task.id]);
        plotMarkers.push(markers[task.id].pickupIconURL);
        plotMarkers.push(markers[task.id].dropoffIconURL);
      }
    });

    options['paths'] = plotPaths;
    options['markers'] = plotMarkers;
    options['mapSize'] = options['mapSize'] ?? '280x320';
    options['selectedTaskId'] = options['selectedTaskId'];

    return this.getStaticMapURL(options);
  }

  async GetRoutePolyLine({
    fromLatitude, // Number
    fromLongitude, // Number
    toLatitude, // Number
    toLongitude, // Number
    departureTime, // DD/MM/YY HH:mm
  }) {
    let request = {
      origin: { lat: fromLatitude, lng: fromLongitude },
      destination: { lat: toLatitude, lng: toLongitude },
      travelMode: 'DRIVING',
      avoidTolls: false,
      avoidHighways: false,
      earliestDeparture: departureTime,
    };

    const directions = await this.GetDirections(request);

    let polyLine = null;

    if (directions && directions.routes && directions.routes.length > 0) {
      polyLine = directions.routes[0].overview_polyline.points;
    }

    return polyLine;
  }

  public GetDirections(request: any) {
    let requestAsQueryString = this.toQueryString(request);
    this.logger.trace('GetDirections()', request, requestAsQueryString);

    return this.http
      .get<any>(`api/map/directions?routeDirectionObject=${requestAsQueryString}`)
      .toPromise();
  }

  public toQueryString(request: any) {
    return encodeURIComponent(JSON.stringify(request));
  }

  public GetAddressFromCoord(lat: number, lng: number) {
    return this.http.get<any>('api/map/reversegeocode?lat=' + lat + '&lng=' + lng).toPromise();
  }

  public GetAddress(address: string): Promise<AddressDTO> {
    const encodedAddress = encodeURIComponent(address);
    this.logger.trace('GetAddress()', address, encodedAddress);
    return this.http.get<AddressDTO>(`api/map/geocode?address=${encodedAddress}`).toPromise();
  }

  public GetAddressObservable(address: string): Observable<GeocoderResponse> {
    const encodedAddress = encodeURIComponent(address);
    this.logger.trace('GetAddress()', address, encodedAddress);
    return this.http.get<GeocoderResponse>(`api/map/geocode?address=${encodedAddress}`);
  }

  public GetPCAKey() {
    return this.http.get<any>('api/map/pcasettings').toPromise();
  }

  public getMiles(meters): number {
    return Math.round(parseFloat((meters * 0.000621371192).toFixed(2)));
  }

  public getTravelTimeFormatted(estimatedTime) {
    var t = estimatedTime.split(':');
    var seconds = +t[0] * 60 * 60 + +t[1] * 60 + +t[2];

    return this.getDateTime(seconds);
  }

  public getDateTime(sec, hourLabel: string = 'hr(s)', minLabel: string = 'min'): string {
    var sec_num = parseInt(sec, 10);
    var hours = Math.floor(sec_num / 3600) % 24;
    var minutes = Math.floor(sec_num / 60) % 60;
    var seconds = sec_num % 60;

    var time = '';

    if (hours == 1) {
      time += hours + ' hr ';
    } else if (hours == 0 || hours > 1) {
      time += hours + ' hrs ';
    } else {
      time += hours + ' ' + hourLabel + ' ';
    }

    if (minutes > 0) {
      time += minutes + ' ' + minLabel + ' ';
    }

    time = time.trim();

    return time;
  }

  //  lineColours: string[]: An array of colours to use for each line. If not provided, the default colours will be used.
  //    colours should be in the format 0xRRGGBBAA, where RR, GG, BB, and AA are the red, green, blue, and alpha values respectively.
  public getStaticMapURL(mapURLOpts: {
    mapType?: string;
    markers?: string[];
    mapSize?: string;
    scale?: 1 | 2;
    paths?: string[];
    polylines?: string[];
    styles?: string[];
  }) {
    const mapType = mapURLOpts['mapType'] ?? 'roadmap';
    const markers = mapURLOpts['markers'] ?? [];
    const mapSize = mapURLOpts['mapSize'] ?? '320x200';
    const scale = mapURLOpts['scale'] ?? '1';
    const paths = mapURLOpts['paths'] ?? [];
    const polylines = mapURLOpts['polylines'] ?? [];
    const styles = mapURLOpts['styles'] ?? [];

    let _url = `${this.host}/${this.baseUrl}/v1/staticmap`;
    let _markers = '';
    let _paths = '';

    _url += `?maptype=${mapType}`;

    if (markers) {
      markers.forEach((val, index) => {
        _markers += `&markers=${val}`;
      });
      _url += `${_markers}`;
    }

    if (mapSize != null) _url += `&size=${mapSize}`;
    if (scale != null) _url += `&scale=${scale}`;

    if (polylines.length > 0) {
      polylines.forEach((polyline: string, index: number) => {
        paths.push('enc%3a' + polylines[index]);
      });
    }

    if (paths.length > 0) {
      paths.forEach((path, index) => {
        _url += '&path=' + path;
      });
    }

    if (styles.length > 0) {
      styles.forEach((style, index) => {
        _url += '&style=' + style;
      });
    }

    return _url;
  }

  public getStaticMapPath(
    mapType: string,
    markers: string[],
    size: string,
    scale: number,
    paths: string[],
    polylines?: string[],
  ) {
    let _url = `${this.host}/${this.baseUrl}/v1/staticmap`;
    let _markers = '';
    let _paths = '';

    _url += `?maptype=${mapType}`;
    if (markers) {
      markers.forEach((val, index) => {
        _markers += `&markers=${val}`;
      });
      _url += `${_markers}`;
    }
    if (size != null) _url += `&size=${size}`;
    if (scale != null) _url += `&scale=${scale}`;
    if (paths) {
      paths.forEach((val, index) => {
        _paths += `&path=${val}`;
      });
      _url += `${_paths}`;
    }

    if (polylines != null) {
      polylines.forEach((polyline: string) => {
        _url += `&path=weight:7%7Cenc%3A${polyline}`;
      });
    }

    return _url;
  }

  public buildMarkers(job: IInboxJob, currentTaskId: number): string[] {
    if (job && job.tasks) {
      let colours = ['gray', 'silver'];
      let markers: string[] = [];
      let currTask = job.tasks.filter((t) => t.id == currentTaskId);
      let otherTasks = job.tasks.filter((t) => t.id != currentTaskId);
      markers.push(
        `color:blue%7Clabel:P%7C${currTask[0].pickupLocation.latitude},${currTask[0].pickupLocation.longitude}`,
      );
      markers.push(
        `color:red%7Clabel:D%7C${currTask[0].dropoffLocation.latitude},${currTask[0].dropoffLocation.longitude}`,
      );
      if (otherTasks) {
        otherTasks.forEach((curr, index) => {
          const color = 'gray';
          markers.push(
            `color:${color}%7Clabel:P%7C${curr.pickupLocation.latitude},${curr.pickupLocation.longitude}`,
          );
          markers.push(
            `color:${color}%7Clabel:D%7C${curr.dropoffLocation.latitude},${curr.dropoffLocation.longitude}`,
          );
        });
      }
      return markers;
    }
    return null;
  }

  public buildMarkersFromTask(task: ITaskInfo): string[] {
    let colours = ['gray', 'silver'];
    let markers: string[] = [];
    markers.push(
      `color:blue%7Clabel:P%7C${task.pickupLocation.latitude},${task.pickupLocation.longitude}`,
    );
    markers.push(
      `color:red%7Clabel:D%7C${task.dropoffLocation.latitude},${task.dropoffLocation.longitude}`,
    );
    return markers;
  }

  public buildPathAsPolyline(task: ITaskInfo): string[] {
    if (task.route) {
      return [`enc%3A${task.route.polyline}`];
    }

    return [];
  }

  public buildPathsAsPolyline(job: IInboxJob, currentTaskId: number): string[] {
    if (job && job.tasks) {
      let paths: string[] = [];
      let currTaskIndex = job.tasks.findIndex((t) => t.id == currentTaskId);
      let currTask = job.tasks[currTaskIndex];
      let otherTasks = job.tasks.filter((t) => t.id != currentTaskId);
      if (otherTasks.length > 3) {
        let shownTask = [];
        if (currTaskIndex == 0) {
          shownTask.push(job.tasks[currTaskIndex + 1]);
          shownTask.push(job.tasks[currTaskIndex + 2]);
        } else if (currTaskIndex == job.tasks.length - 1) {
          shownTask.push(job.tasks[currTaskIndex - 1]);
          shownTask.push(job.tasks[currTaskIndex - 2]);
        } else {
          shownTask.push(job.tasks[currTaskIndex - 1]);
          shownTask.push(job.tasks[currTaskIndex + 1]);
        }
        otherTasks = shownTask;
      }

      if (currTask && currTask.route.polyline) paths.push(`enc%3A${currTask.route.polyline}`);
      if (otherTasks) {
        otherTasks.forEach((curr, index) => {
          if (curr && curr.route && index < 4)
            if (curr.route) paths.push(`enc%3A${curr.route.polyline}`);
        });
      }
      return paths;
    }
    return null;
  }

  latLngToCombo(task: IStaticMapTask, isPickup: boolean): string {
    const lat = isPickup
      ? task.pickupLocation.latitude ?? task.pickupLocation.postcode ?? ''
      : task.dropoffLocation.latitude ?? task.dropoffLocation.postcode ?? '';

    const lng = isPickup
      ? task.pickupLocation.longitude ?? task.pickupLocation.postcode ?? ''
      : task.dropoffLocation.longitude ?? task.dropoffLocation.postcode ?? '';

    return lat + 'x' + lng;
  }

  public buildAlphaMarkers(tasks: IStaticMapTask[]) {
    let markers: { [key: number]: { pickupIconURL?: string; dropoffIconURL?: string } } = {};

    const icons = this.setAlphaIcons(tasks, 'png');

    tasks.forEach((task) => {
      const pickupIcon = icons[task.id]['pickup'];
      const dropoffIcon = icons[task.id]['dropoff'];

      const pickupIconURL = `icon:${pickupIcon}%7C${task.pickupLocation.latitude},${task.pickupLocation.longitude}`;
      const dropoffIconURL = `icon:${dropoffIcon}%7C${task.dropoffLocation.latitude},${task.dropoffLocation.longitude}`;

      markers[task.id] = { pickupIconURL: pickupIconURL, dropoffIconURL: dropoffIconURL };
    });

    return markers;
  }

  setAlphaIcons(tasks: IStaticMapTask[], fileType = 'png') {
    let alphaIconUrls: { [key: string]: {} } = {};
    let locationList: { taskId: number; latLng: string }[] = [];

    const urlPrefix = environment.maps.staticContentBucket;

    // Get a list (in order of tasks)
    tasks.forEach((t) => {
      const latLng1 = this.latLngToCombo(t, true);
      const latLng2 = this.latLngToCombo(t, false);
      locationList.push({ taskId: t.id, latLng: latLng1 }, { taskId: t.id, latLng: latLng2 });
    });

    // For each task
    tasks.forEach((task) => {
      alphaIconUrls[task.id] = { pickup: '', dropoff: '' };

      // For pickup and dropoff
      [true, false].forEach((isPickup) => {
        const thisTaskLatLng = this.latLngToCombo(task, isPickup);

        // What index is this tasklatlng in the list
        const abc = locationList.findIndex(
          (o) => o.taskId === task.id && o.latLng === thisTaskLatLng,
        );
        const chr = String.fromCharCode(97 + abc);
        const section = {
          1: ['left'],
          2: ['left', 'right'],
          3: ['left', 'middle', 'right'],
        };

        // How many times this location appears in the list -- no more than 3 times allowed
        const locationCount = locationList.filter((o) => o.latLng === thisTaskLatLng).length;

        // Find the index that thistasklatlng is in the list
        const locationIndex = locationList
          .filter((o) => o.latLng === thisTaskLatLng)
          .findIndex((o) => o.taskId === task.id);

        alphaIconUrls[task.id][isPickup ? 'pickup' : 'dropoff'] =
          urlPrefix +
          `${fileType}/` +
          `alpha-icons/` +
          chr +
          '/' +
          locationCount +
          '-' +
          section[locationCount][locationIndex] +
          `.${fileType}`;
      });
    });

    return alphaIconUrls;
  }

  geocodeLatLng(location: google.maps.LatLngLiteral): Promise<GeocoderResponse> {
    let geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: location }, (results, status) => {
        const response = new GeocoderResponse(status, results);
        resolve(response);
      });
    });
  }

  getInteractiveMapDataForRoute(
    routeId: number,
    selectedTaskId?: number,
  ): Promise<InteractiveMapData> {
    const url = `${this.baseUrl}/interactive/route/${routeId}`;

    let params = new HttpParams();
    if (selectedTaskId) {
      params = params.set('selectedTaskId', selectedTaskId.toString());
    }

    return this.http.get<InteractiveMapData>(url, { params }).toPromise();
  }

  getInteractiveMapDataForTasks(
    taskIds: number[],
    selectedTaskId?: number,
  ): Promise<InteractiveMapData> {
    const url = `${this.baseUrl}/interactive/tasks`;

    let params = new HttpParams();

    taskIds.forEach((id) => {
      params = params.append('taskIds', id.toString());
    });

    if (selectedTaskId) {
      params = params.set('selectedTaskId', selectedTaskId.toString());
    }

    return this.http.get<InteractiveMapData>(url, { params }).toPromise();
  }
}
